import { createFileRoute, Link } from "@tanstack/react-router";
import BlogPostLayout, { blogPostHead } from "@/components/BlogPostLayout";
import { getPost } from "@/lib/blog-posts";

const post = getPost("corporate-holiday-party-transportation-planning-guide");

const faqs = [
  {
    q: "How far ahead should I book holiday party transportation in Atlanta?",
    a: "Eight weeks or more is ideal, especially for a Friday or Saturday in December. December weekends are the first dates to go, so send your date as soon as you have it.",
  },
  {
    q: "What information does a transportation company need?",
    a: "Your date, the venue address, the expected headcount, pickup points, the loop schedule with first and last run times, any executive arrivals, and one on-site contact for the night.",
  },
  {
    q: "Should I book group loops or executive cars?",
    a: "Usually both. Group loops in a Sprinter move most guests between the office or hotel and the venue, while an Executive Style Sprinter or Suburban SUV handles leadership arrivals and VIP guests.",
  },
];

export const Route = createFileRoute("/blog/corporate-holiday-party-transportation-planning-guide")({
  head: () => blogPostHead(post, faqs),
  component: Post,
});

function Post() {
  return (
    <BlogPostLayout post={post} faqs={faqs}>
      <p>
        If you are an executive assistant or office manager, the holiday party probably landed on your desk along
        with everything else. The venue and the menu get most of the attention. Transportation tends to come last,
        and in Atlanta in December, last is often too late.
      </p>
      <p>
        This guide walks you through a simple timeline, the exact details to send your transportation company, and
        how to decide between group loops and executive cars. It's built from the questions planners ask us every
        year.
      </p>

      <h2>Why Transportation Deserves a Spot Early on Your List</h2>
      <p>
        Good transportation does three jobs at once. It gets guests to the venue on time, it gives leadership a
        smooth arrival, and it gets everyone home safely after the party. That last part matters most. When the
        company provides the ride home, nobody has to make a hard decision at the end of the night.
      </p>
      <p>
        It also takes a real weight off you. With one point of contact handling every vehicle, you can focus on
        the party instead of answering texts about where the ride is.
      </p>

      <h2>The Timeline</h2>
      <h3>8+ weeks out: lock the date</h3>
      <p>
        As soon as the date and venue are set, contact your transportation company. December weekends are the
        first dates to go, and the vehicles best suited to corporate groups book quickly. At this stage you only
        need the date, the venue and a rough headcount to hold a vehicle.
      </p>
      <ul>
        <li>Send the date, the venue and an estimated headcount.</li>
        <li>Ask what vehicles are still open for your date.</li>
        <li>Ask who your single point of contact will be.</li>
      </ul>

      <h3>4 to 6 weeks out: shape the plan</h3>
      <p>
        By now you should know roughly how many guests are coming and where they will start. This is when you
        decide how many vehicles you need and draft the loop schedule.
      </p>
      <ul>
        <li>Confirm pickup points: the office, a hotel block, or both.</li>
        <li>Draft first and last run times, working backward from the party start.</li>
        <li>List any executives or VIP guests who need their own arrival.</li>
        <li>Get the quote and terms in writing.</li>
      </ul>

      <h3>2 weeks out: confirm the details</h3>
      <p>
        Lock the loop schedule, confirm the executive arrivals and send any venue notes, such as where vehicles
        should stage or which entrance guests will use.
      </p>
      <ul>
        <li>Send the final loop schedule.</li>
        <li>Share venue loading and staging instructions.</li>
        <li>Name one on-site contact from your team for the night.</li>
      </ul>

      <h3>The week of: final headcount</h3>
      <p>
        Send the final headcount and any last changes to pickup points. Share the pickup times with guests so
        they know when and where to meet the vehicle. A short calendar invite works well.
      </p>

      <h3>The night of: stay reachable</h3>
      <p>
        Keep your transportation contact's number handy and let them know if the party runs long. Plan staggered
        return runs instead of one final departure, so guests who leave early aren't waiting, and the last guests
        still have a ride home.
      </p>

      <h2>The Checklist: What to Send Your Transportation Company</h2>
      <ul>
        <li>
          <strong>Date</strong> and party start and end times
        </li>
        <li>
          <strong>Venue</strong> address, plus staging or entrance notes
        </li>
        <li>
          <strong>Headcount</strong>, estimated first and final later
        </li>
        <li>
          <strong>Pickup points</strong>: office, hotel, or both
        </li>
        <li>
          <strong>Loop schedule</strong>: first run, last run, and return runs
        </li>
        <li>
          <strong>Executive arrivals</strong>: names, pickup addresses and times
        </li>
        <li>
          <strong>On-site contact</strong> from your team for the night
        </li>
        <li>
          <strong>Billing details</strong>, or your corporate account information
        </li>
      </ul>

      <h2>Group Loops or Executive Cars?</h2>
      <p>
        Most holiday parties need a mix of both. Here is how to think about it.
      </p>
      <h3>Group loops</h3>
      <p>
        A chauffeured Sprinter running a set schedule between your office or hotel and the venue moves most guests
        efficiently. Everyone arrives together, nobody has to find parking, and the return runs get people home.
        Our <Link to="/sprinter-van-service-atlanta">Sprinter service</Link> page explains which Sprinter fits
        which kind of group.
      </p>
      <h3>Executive cars</h3>
      <p>
        Leadership, speakers and VIP guests usually deserve their own arrival. An Executive Style Sprinter or a
        Suburban SUV gives them a quiet, private ride on their own schedule, often from a different starting
        point than everyone else.
      </p>
      <h3>How to decide</h3>
      <ul>
        <li>Most guests starting from one or two places: group loops.</li>
        <li>Leadership arriving separately or on a tight schedule: an executive car.</li>
        <li>Guests leaving at different times: staggered return runs.</li>
      </ul>

      <h2>Choosing the Right Company</h2>
      <p>
        Ask who owns the vehicles and whether a subcontractor might show up. For corporate events especially, you
        want the vehicle and chauffeur you book to be the ones that arrive. Our guide{" "}
        <Link to="/blog/questions-to-ask-before-paying-a-limo-deposit">9 Questions to Ask Before You Pay a Limo Deposit</Link>{" "}
        covers what to ask.
      </p>
      <p>
        At Atlanta Luxury Sprinters, we own every vehicle we send, our chauffeurs are background-checked,
        fingerprinted and long-tenured, and you get one point of contact from planning to the last ride home. See
        our <Link to="/corporate-holiday-party-transportation-atlanta">corporate holiday party transportation</Link>{" "}
        page, or set up a <Link to="/corporate-accounts">corporate account</Link> for travel all year.
      </p>
      <p>
        December weekends are the first dates to go. Send your date now and we'll tell you what's still open.
      </p>
    </BlogPostLayout>
  );
}
